import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import DatePickerModal from "../../components/DatePickerModal/DatePickerModal";
import RotateDevice from "../../components/RotateDevice/RotateDevice";
import Lf23Loader from "../../components/Lf23Loader";
import {
    API_URL, authHeaders, formatData, statoBadgeClass, FiltroMulti, valoriDi, opzioniDa, FullscreenBtn, KpiCard, ico,
} from "./comuni";
import {
    AggiornaBtn, CardPercorso, RigaPercorso, TestataPercorsi, ViewToggle, useEscChiude, STATI_CONCLUSI, formatDataOra,
    isAperto, riaperturaNonRichiusa,
} from "./percorsiCondivisi";
import ModaleChiusuraPercorso from "./ModaleChiusuraPercorso";

/**
 * Riga di stato della mail delle 09:00 dentro l'avviso (come la riga di stato delle bozze).
 * `info` = campo `mail` di GET /percorsi/da-chiudere. Ritorna {testo, errore} o null.
 * L'ordine dei casi conta: prima quello che richiede un intervento (sospeso, fallito).
 */
const statoMail = (info) => {
    if (!info) return null;
    const { jobAttivo, mailAbilitata, oraInvio, maxTentativi, erroriOggi, sospesoOggi, ultimo } = info;
    const quando = ultimo ? formatDataOra(ultimo.avviato_il) : "";
    if (!jobAttivo) return { testo: "La mail giornaliera dei percorsi da chiudere non è attiva in questo ambiente." };
    if (sospesoOggi) {
        return { errore: true, testo: `⚠ Mail delle ${oraInvio} sospesa per oggi dopo ${maxTentativi} tentativi falliti${ultimo?.errore ? `: ${ultimo.errore}` : ""}. Si riprova domani.` };
    }
    if (!ultimo) return { testo: `Mail automatica ogni mattina alle ${oraInvio}: nessun invio ancora eseguito.` };
    if (ultimo.esito === "errore") {
        return { errore: true, testo: `⚠ Ultimo invio fallito (${quando})${ultimo.errore ? `: ${ultimo.errore}` : ""}. Tentativi oggi ${erroriOggi}/${maxTentativi}, il sistema riprova ogni 30 minuti.` };
    }
    if (ultimo.n_percorsi === 0) return { testo: `Mail automatica alle ${oraInvio} · ultimo controllo ${quando}: nessun percorso da chiudere, nessuna mail inviata.` };
    if (!mailAbilitata || (ultimo.errore || "").startsWith("dry-run")) {
        return { testo: `Mail automatica alle ${oraInvio} · ultimo giro ${quando} in prova (MAIL_ENABLED disattivo): la mail non è stata spedita.` };
    }
    return { testo: `Mail automatica alle ${oraInvio} · ultima inviata ${quando} a ${ultimo.destinatari || "—"} (${ultimo.n_percorsi} ${ultimo.n_percorsi === 1 ? "percorso" : "percorsi"}).` };
};

/**
 * Sotto-tab "Storico" del tab Percorsi (task 517).
 *  - In cima l'avviso "Da chiudere": percorsi con data passata rimasti "Da Fare" / "In
 *    Lavorazione" (l'operatore non ha premuto "Completa"). Non segue i filtri: è un avviso.
 *    Da qui si chiudono col pulsante "Chiudi" (ModaleChiusuraPercorso).
 *  - Sotto la tabella dei conclusi (data passata), con filtri Dal/Al/Stato, ricerca,
 *    ordinamento e paginazione lato server — è la vecchia tabella "parziali / completati".
 *    I KPI seguono i filtri (decisione con Gianni), tranne "Da chiudere".
 *
 * Props: viewMode/onViewMode, onDetail, azioniDi(p, opts) — come SezioneOggi.
 */
const STATS_VUOTE = { totali: 0, nonEseguiti: 0, parziali: 0, completati: 0, riaperti: 0, daChiudere: 0 };

const SezioneStorico = ({ zona, viewMode, onViewMode, onDetail, azioniDi }) => {
    // ---- tabella dei conclusi (paginata dal backend) ----
    const [dati, setDati] = useState([]);
    const [stats, setStats] = useState(STATS_VUOTE);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [fs, setFs] = useState(false);

    const [showFilters, setShowFilters] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [query, setQuery] = useState({ search: "", fromDate: "", toDate: "", stato: "", sortKey: "", sortDir: "asc" });
    // pannello filtri (applicati su "Applica")
    const [fFrom, setFFrom] = useState("");
    const [fTo, setFTo] = useState("");
    const [fStato, setFStato] = useState([]);

    const fetchData = useCallback(async (opts = {}) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/percorsi`, {
                headers: authHeaders(),
                params: {
                    zona,
                    page,
                    limit,
                    search: query.search || undefined,
                    fromDate: query.fromDate || undefined,
                    toDate: query.toDate || undefined,
                    stato: query.stato || undefined,
                    sortKey: query.sortKey || undefined,
                    sortDir: query.sortDir || undefined,
                    refresh: opts.refresh ? 1 : undefined,
                },
            });
            setDati(res.data?.data ?? []);
            const pag = res.data?.pagination ?? {};
            setTotalPages(pag.totalPages ?? 1);
            setTotalItems(pag.totalItems ?? 0);
            setStats(res.data?.stats ?? STATS_VUOTE);
        } catch (err) {
            console.error("Errore nel fetch storico percorsi:", err);
            setDati([]);
        } finally {
            setLoading(false);
        }
    }, [zona, page, limit, query]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ---- avviso "Da chiudere" (endpoint a parte: sono pochi e vanno visti tutti) ----
    const [daChiudere, setDaChiudere] = useState([]);
    const [tratteDisponibili, setTratteDisponibili] = useState(true);
    const [mailInfo, setMailInfo] = useState(null); // stato della mail delle 09:00
    const [dcLoading, setDcLoading] = useState(false);
    const [daChiudereAperto, setDaChiudereAperto] = useState(null); // riga nel modale di chiusura
    const [esitoChiusura, setEsitoChiusura] = useState(null);       // messaggio dopo una chiusura ok
    const avvisoRef = useRef(null);

    const fetchDaChiudere = useCallback(async (opts = {}) => {
        setDcLoading(true);
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/percorsi/da-chiudere`, {
                headers: authHeaders(),
                params: { zona, refresh: opts.refresh ? 1 : undefined },
            });
            setDaChiudere(res.data?.data ?? []);
            setTratteDisponibili(res.data?.tratteDisponibili !== false);
            setMailInfo(res.data?.mail ?? null);
        } catch (err) {
            console.error("Errore nel fetch percorsi da chiudere:", err);
            setDaChiudere([]);
        } finally {
            setDcLoading(false);
        }
    }, [zona]);

    useEffect(() => { fetchDaChiudere(); }, [fetchDaChiudere]);

    // "Aggiorna": le due liste insieme. Mandano entrambe refresh=1 ma il backend coalesce le
    // richieste in volo, quindi Interacta viene letta una volta sola.
    const aggiorna = () => { fetchDaChiudere({ refresh: true }); fetchData({ refresh: true }); };

    // Chiusura riuscita: il percorso passa dall'avviso alla tabella (il backend ha già
    // svuotato la sua cache, basta rileggere).
    const chiuso = (r) => {
        setDaChiudereAperto(null);
        setEsitoChiusura(`${r.customId} chiuso come "${r.statoA}" (${r.tratteFatte}/${r.tratteTotali} tratte fatte).`);
        fetchDaChiudere();
        fetchData();
    };

    // debounce ricerca libera
    useEffect(() => {
        const t = setTimeout(() => {
            setQuery((q) => (q.search === searchInput ? q : { ...q, search: searchInput }));
            setPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const chiudiFs = useCallback(() => setFs(false), []);
    useEscChiude(fs, chiudiFs);

    const applyFilters = () => {
        setQuery((q) => ({ ...q, search: searchInput, fromDate: fFrom, toDate: fTo, stato: valoriDi(fStato).join(",") }));
        setPage(1);
    };
    const resetFilters = () => {
        setFFrom(""); setFTo(""); setFStato([]); setSearchInput("");
        setQuery({ search: "", fromDate: "", toDate: "", stato: "", sortKey: "", sortDir: "asc" });
        setPage(1);
    };

    const toggleSort = (key) => {
        setQuery((q) => ({ ...q, sortKey: key, sortDir: q.sortKey === key && q.sortDir === "asc" ? "desc" : "asc" }));
        setPage(1);
    };
    const sortArrow = (key) => (query.sortKey === key ? (query.sortDir === "asc" ? " ▲" : " ▼") : "");

    // Un riaperto "in correzione" (task 532) è di nuovo In Lavorazione: niente PDF, come in Oggi.
    const azioni = (p) => azioniDi(p, { conReport: !isAperto(p.stato) });
    // I riaperti sono in lista ma non nel conteggio (stats.totali): lo si dice accanto al numero.
    const titolo = (
        <>
            Storico percorsi <span className="gp-section-count">{stats.totali}</span>
            {stats.riaperti > 0 && (
                <span className="gp-riaperti-count" title="Riaperti dall'operatore per una correzione: in lista, ma fuori dai conteggi finché non li richiude">
                    + {stats.riaperti} {stats.riaperti === 1 ? "riaperto" : "riaperti"} in correzione
                </span>
            )}
        </>
    );

    // Paginazione condivisa tra vista tabella e vista card (stesso identico markup).
    const paginationBar = (
        <div className="pagination">
            <button className="page-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg> Prec
            </button>
            <span className="page-info">Pag <strong>{page}</strong> di <strong>{totalPages}</strong></span>
            <span className="page-total">({totalItems} percorsi totali)</span>
            <button className="page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Succ <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
            </button>
            <div className="gp-perpage">
                <span>Per pagina:</span>
                {[10, 20, 50, 100].map((n) => (
                    <button key={n} className={`gp-perpage-btn ${limit === n ? "active" : ""}`} onClick={() => { setLimit(n); setPage(1); }}>{n}</button>
                ))}
            </div>
        </div>
    );

    // Bottone "Chiudi" di una riga dell'avviso: spento se lo stato finale non è calcolabile.
    const btnChiudi = (p) => (
        <button
            className="gp-dettaglio-btn"
            onClick={(e) => { e.stopPropagation(); setEsitoChiusura(null); setDaChiudereAperto(p); }}
            disabled={!p.statoProposto}
            title={p.statoProposto ? `Chiudi come "${p.statoProposto}"` : "Tratte fatte non leggibili: chiusura non disponibile"}
        >
            Chiudi
        </button>
    );
    const rigaMail = statoMail(mailInfo);
    const ritardo = (p) => `${p.giorniRitardo} ${p.giorniRitardo === 1 ? "giorno" : "giorni"}`;
    const tratte = (p) => (p.tratteFatte == null ? `? / ${p.tratteTotali}` : `${p.tratteFatte} / ${p.tratteTotali}`);

    return (
        <>
            <div className="gp-toolbar-row">
            <button className={`toggle-filters-btn ${showFilters ? "active" : ""}`} onClick={() => setShowFilters((v) => !v)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
                {showFilters ? "Nascondi Filtri" : "Mostra Filtri"}
                <svg className="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
            </button>

            {showFilters && (
                <div className="filters-panel">
                    <div className="filters-grid">
                        <div className="filter-group">
                            <label>Dal</label>
                            <DatePickerModal value={fFrom} onChange={(e) => setFFrom(e.target.value)} max={fTo || undefined} label="Dal" />
                        </div>
                        <div className="filter-group">
                            <label>Al</label>
                            <DatePickerModal value={fTo} onChange={(e) => setFTo(e.target.value)} min={fFrom || undefined} label="Al" />
                        </div>
                        <div className="filter-group">
                            <label>Stato</label>
                            <FiltroMulti placeholder="Tutti" options={opzioniDa(STATI_CONCLUSI)}
                                value={fStato} onChange={setFStato} />
                        </div>
                    </div>
                    <div className="filter-actions">
                        <button className="btn btn-ghost" onClick={resetFilters}>Reset</button>
                        <button className="btn btn-primary" onClick={applyFilters}>Applica</button>
                    </div>
                </div>
            )}

            <div className="toolbar">
                <div className="search-box">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                    <input type="text" placeholder="Cerca percorso, customId..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
                </div>
                <span className="gp-tb-break" aria-hidden="true" />
                <div className="spacer" />
                <AggiornaBtn onClick={aggiorna} />
            </div>
            </div>

            {loading && dati.length === 0 ? (
                <div className="gp-loading">
                    <Lf23Loader testo="Caricamento storico percorsi..." />
                </div>
            ) : (
            <>
            <div className="stats-grid">
                {/* "Da chiudere" non segue i filtri ed è cliccabile: porta all'avviso. */}
                <div
                    className="gp-kpi-link"
                    role="button"
                    tabIndex={0}
                    onClick={() => avvisoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                    onKeyDown={(e) => { if (e.key === "Enter") avvisoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                >
                    <KpiCard tone={stats.daChiudere > 0 ? "red" : "slate"} label="Da chiudere" valore={stats.daChiudere}
                        titolo="Percorsi con data passata non chiusi dall'operatore, compresi i riaperti e non più richiusi da 3 giorni (non segue i filtri)"
                        icona={ico(<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>)} />
                </div>
                <KpiCard tone="green" label="Completati" valore={stats.completati}
                    icona={ico(<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>)} />
                <KpiCard tone="amber" label="Parziali" valore={stats.parziali}
                    icona={ico(<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>)} />
                <KpiCard tone="slate" label="Non eseguiti" valore={stats.nonEseguiti}
                    icona={ico(<><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>)} />
            </div>

            {esitoChiusura && (
                <div className="gp-chiusura-ok">
                    <span>✓ {esitoChiusura}</span>
                    <button className="modal-close" onClick={() => setEsitoChiusura(null)} title="Chiudi messaggio">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                </div>
            )}

            {/* ===== AVVISO "DA CHIUDERE" ===== solo se ce n'è almeno uno */}
            {daChiudere.length > 0 && (
                <div className="gp-dachiudere" ref={avvisoRef}>
                    <div className="gp-dachiudere-head">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                        <div>
                            <strong>Percorsi da chiudere ({daChiudere.length})</strong>
                            <p>
                                La data è passata ma l'operatore non li ha chiusi dall'app (mai chiusi, o riaperti per una
                                {" "}correzione e non più richiusi da 3 giorni): finché restano aperti non
                                {" "}contano in Reportistica né nelle Tratte da recuperare. "Chiudi" li porta nello stato
                                {" "}giusto in base alle tratte fatte.
                            </p>
                            {!tratteDisponibili && (
                                <p className="gp-dachiudere-warn">
                                    ⚠ Le tratte fatte non sono leggibili in questo momento (database dell'app degli operatori):
                                    {" "}la chiusura è sospesa finché non torna disponibile.
                                </p>
                            )}
                        </div>
                    </div>
                    {rigaMail && (
                        <div className={`gp-bozze-status gp-dachiudere-mail${rigaMail.errore ? " is-errore" : ""}`}>
                            <span>{rigaMail.testo}</span>
                        </div>
                    )}
                    {viewMode === "table" ? (
                        <div className="table-scroll">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Percorso</th>
                                        <th>Data</th>
                                        <th>Ritardo</th>
                                        <th>Assegnatario</th>
                                        <th>Stato attuale</th>
                                        <th>Tratte fatte</th>
                                        <th>Chiuderà come</th>
                                        <th className="col-actions">Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {daChiudere.map((p) => (
                                        <tr key={p.id} className="gp-row-click" onClick={() => onDetail(p)} title="Vedi dettaglio percorso">
                                            <td><span className="strong">{p.nome || "—"}</span><div className="codice" style={{ fontSize: 11 }}>{p.customId || "—"}</div></td>
                                            <td>{formatData(p.data)}</td>
                                            <td>{ritardo(p)}</td>
                                            <td>{p.assegnatario?.nome || "—"}</td>
                                            <td>
                                                <span className={`badge ${statoBadgeClass(p.stato)}`}>{p.stato || "—"}</span>
                                                {p.riapertura && <div className="gp-riaperto-nota">{riaperturaNonRichiusa(p.riapertura)}</div>}
                                            </td>
                                            <td>{tratte(p)}</td>
                                            <td>{p.statoProposto ? <span className={`badge ${statoBadgeClass(p.statoProposto)}`}>{p.statoProposto}</span> : <span className="muted">—</span>}</td>
                                            <td className="col-actions" onClick={(e) => e.stopPropagation()}>{btnChiudi(p)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="gp-cards-grid">
                            {daChiudere.map((p) => (
                                <div className="gp-card" key={p.id}>
                                    <div className="gp-card-clickable" onClick={() => onDetail(p)} title="Vedi dettaglio percorso">
                                        <div className="gp-card-head">
                                            <div className="gp-card-title">{p.nome || "—"}<span className="codice">{p.customId || "—"}</span></div>
                                            <span className={`badge ${statoBadgeClass(p.stato)}`}>{p.stato || "—"}</span>
                                        </div>
                                        <div className="gp-card-body">
                                            {p.riapertura && <div className="gp-riaperto-nota">{riaperturaNonRichiusa(p.riapertura)}</div>}
                                            <div className="gp-card-row"><span className="gp-card-label">Data</span><span className="gp-card-value">{formatData(p.data)} · {ritardo(p)}</span></div>
                                            <div className="gp-card-row"><span className="gp-card-label">Assegnatario</span><span className="gp-card-value">{p.assegnatario?.nome || "—"}</span></div>
                                            <div className="gp-card-row"><span className="gp-card-label">Tratte fatte</span><span className="gp-card-value">{tratte(p)}</span></div>
                                            <div className="gp-card-row"><span className="gp-card-label">Chiuderà come</span><span className="gp-card-value">{p.statoProposto || "—"}</span></div>
                                        </div>
                                    </div>
                                    <div className="gp-card-actions">{btnChiudi(p)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {dcLoading && daChiudere.length === 0 && <div className="gp-empty">Controllo percorsi da chiudere…</div>}

            <ViewToggle viewMode={viewMode} onChange={onViewMode} />

            {viewMode === "table" ? (
            <div className={`table-container${fs ? " gp-fullscreen-table" : ""}`}>
                <RotateDevice active={fs} maxWidth={1024} />
                <div className="gp-table-topbar">
                    <div className="gp-table-title">{titolo}</div>
                    <FullscreenBtn attivo={fs} onClick={() => setFs((v) => !v)} />
                </div>
                <div className="table-scroll">
                    <table>
                        <TestataPercorsi sortable={{ toggleSort, sortArrow }} />
                        <tbody>
                            {dati.map((p) => <RigaPercorso key={p.id} p={p} onDetail={onDetail} azioni={azioni(p)} />)}
                        </tbody>
                    </table>
                    {!loading && dati.length === 0 && <div className="gp-empty">Nessun percorso trovato.</div>}
                </div>
                {paginationBar}
            </div>
            ) : (
            <>
                <div className="gp-section-head">{titolo}</div>
                {dati.length === 0 ? (
                    <div className="gp-empty">Nessun percorso trovato.</div>
                ) : (
                    <div className="gp-cards-grid">
                        {dati.map((p) => <CardPercorso key={p.id} p={p} onDetail={onDetail} azioni={azioni(p)} />)}
                    </div>
                )}
                {paginationBar}
            </>
            )}
            </>
            )}

            {daChiudereAperto && (
                <ModaleChiusuraPercorso
                    percorso={daChiudereAperto}
                    onClose={() => setDaChiudereAperto(null)}
                    onChiuso={chiuso}
                />
            )}
        </>
    );
};

export default SezioneStorico;
